import { Controller } from "@hotwired/stimulus";

/**
 * La carte d'une animation du panneau Animations (habillage vidéo).
 *
 * La carte porte l'index de son animation dans l'habillage (`index` value, posé par le
 * serveur) : c'est par lui qu'elle désigne l'animation qu'elle édite. Les changements partent
 * en événements, que l'éditeur de composition applique à l'habillage sélectionné (comme
 * l'ajout, overlay:add-anim). La carte montre ses propres éditions tout de suite ; le serveur
 * confirme à la sauvegarde.
 *
 * Naming: "composition-editor--overlay-animation" in HTML.
 */
export default class extends Controller {
  static targets = ["duration", "display", "side"];

  static values = { index: Number };

  /** La durée affichée suit le curseur pendant qu'on le tire (sans muter le document). */
  preview() {
    if (!this.hasDisplayTarget) return;
    const value = parseFloat(this.durationTarget.value);
    const text = this.displayTarget.textContent.trim();
    // Le gabarit vient du serveur (« 0,6 s ») : on ne remplace que le nombre, à sa décimale.
    const decimal = /\d([.,])\d/.exec(text)?.[1] || ".";
    this.displayTarget.textContent = text.replace(/\d+(?:[.,]\d+)?/, value.toFixed(1).replace(".", decimal));
  }

  /** Le curseur relâché : la durée part au document. */
  change() {
    this._dispatch("overlay:set-anim-duration", {
      duration: parseFloat(this.durationTarget.value) || 0.5,
    });
  }

  /** Entrée | Sortie : le bloc blanc glisse tout de suite, le document suit. */
  setSide({ params: { side } }) {
    if (!this.hasSideTarget || this.sideTarget.dataset.side === side) return;
    this.sideTarget.dataset.side = side;
    this.sideTarget.querySelectorAll("[role=radio]").forEach((radio) => {
      radio.setAttribute("aria-checked", String(radio.dataset.compositionEditorOverlayAnimationSideParam === side));
    });
    this._dispatch("overlay:set-anim-side", { side });
  }

  /**
   * Un réglage propre au preset (`options` du catalogue — la couleur d'un balayage) : le
   * champ qui change porte sa clé en paramètre, sa valeur est celle du champ.
   */
  setOption({ params: { key }, target }) {
    if (!key) return;
    this._dispatch("overlay:set-anim-option", { key, value: target.value });
  }

  remove() {
    this._dispatch("overlay:remove-anim");
    this.element.remove();
  }

  // ==================== Private ====================

  _dispatch(name, detail = {}) {
    window.dispatchEvent(new CustomEvent(name, { detail: { index: this.indexValue, ...detail } }));
  }
}
